import React from 'react';

import type { RowType } from 'src/components/PageBuilder/components';

import { getAdvancedSettings } from '../advancedSettings';
import { getBackgroundSettings } from '../backgroundSettings';
import { useImageDimensions } from '../../utils';

import { Row } from './components';

type PageBuilderRowProps = RowType;

/**
 * Return the PB Row Block
 * @param props
 * @constructor
 */
export function PageBuilderRow(props: PageBuilderRowProps) {
    /**
     * Configure the styles requires for the Block
     */
    const backgroundStyles = getBackgroundSettings<PageBuilderRowProps>({ ...props });
    const advancedSettings = getAdvancedSettings<PageBuilderRowProps>(props);

    const mobileImageDimension = useImageDimensions(props.mobileImage);
    const desktopImageDimensions = useImageDimensions(props.desktopImage);

    const layoutStyles = {
        minHeight: props.minHeight,
    };

    if (props.verticalAlignment) {
        layoutStyles['display'] = 'flex';
        layoutStyles['flexDirection'] = 'column';
        layoutStyles['justifyContent'] = props.verticalAlignment;
    }

    const backgroundId = props.appearance === 'contained' ? `container-${props.uniqueID}` : props.uniqueID;

    const backgroundImageStyles = React.useMemo(
        () => `
        #${backgroundId} {
            background-image: url(${props.mobileImage ?? props.desktopImage});
            aspect-ratio: ${mobileImageDimension?.width ?? desktopImageDimensions?.width} / ${
            mobileImageDimension?.height ?? desktopImageDimensions?.height
        };width: 100%;}
    
        @media (min-width: 900px) {
            #${backgroundId} {
                background-image: url(${props.desktopImage});
                aspect-ratio: ${desktopImageDimensions?.width} / ${desktopImageDimensions?.height};
            }
        }
    `,
        [
            backgroundId,
            props.mobileImage,
            props.desktopImage,
            mobileImageDimension?.width,
            mobileImageDimension?.height,
            desktopImageDimensions?.width,
            desktopImageDimensions?.height,
        ],
    );

    /**
     * Render the Row Block UI
     */
    return (
        <>
            {props.desktopImage && <style dangerouslySetInnerHTML={{ __html: backgroundImageStyles }} />}
            <Row
                styles={{
                    ...backgroundStyles,
                    ...advancedSettings,
                    ...layoutStyles,
                }}
                cssClasses={props.cssClasses}
                appearance={props.appearance ?? 'contained'}
                {...props}
            >
                {props.children}
            </Row>
        </>
    );
}
